import { defer, EMPTY, from, Observable, of } from "rxjs"
import { Router } from "../../../server/router"
import type { EpicDependencies } from "../../rxjs/configureEpics"
import { SIGN_UP_FAILURE, SIGN_UP_FORM_SUBMITTED, SIGN_UP_SUCCESS, signUpFailure, signUpSuccess } from "./actions"
import { createLoadingEnded, createLoadingStarted } from "../../filters/actions"
import type { LoginResponsePayload } from "../../../api/login"
import { signInFailure, signInSuccess } from "../login/actions"
import { createEventRequest, requestEvent } from "../../../events/utils"
import { getHttpHeaders } from "../../auth/selectors"
import { EventType } from "../../../events/types"
import { EventDataResponse } from "../../../graphql/types"
import { NotificationType, openNotification } from "../../uiKit/Notification/Notification"
import { ofType, StateObservable } from "redux-observable"
import { catchError, delay, switchMap } from "rxjs/operators"
import { Action, StateObject } from "../../redux/types"

export const signUpSubmitted = (actions$: Observable<Action>) =>
    actions$.pipe(
        ofType(SIGN_UP_FORM_SUBMITTED),
        switchMap(() => {
            Router.push("/sign-up")
            return [createLoadingStarted("sign-up-form")]
        })
    )

export const signUpRequested = (
    actions$: Observable<Action>,
    state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(SIGN_UP_FORM_SUBMITTED),
        switchMap((action: Action) => {
            const email = action.payload.values.email
            const password = action.payload.values.password
            const phone = action.payload.values.phoneNumber
            const phoneNumber = phone ? phone.replace(/\D/g, "") : phone
            const nameOfBusiness = action.payload.values.nameOfBusiness
            const businessType = action.payload.values.businessType

            return defer(() =>
                requestEvent(
                    getHttpHeaders(deps.getState()),
                    createEventRequest(
                        EventType.SignupRequested,
                        {
                            correlationId: deps.idgen.next(),
                        },
                        {
                            email: email,
                            password: password,
                            phoneNumber: phoneNumber,
                            nameOfBusiness: nameOfBusiness,
                            businessType: businessType,
                        },
                        0
                    )
                )
            ).pipe(
                switchMap((res: EventDataResponse) => {
                    const id = res?.id
                    if (!id) {
                        throw new Error("signUpRequestedEpic - Id is missing")
                    }

                    // TODO Hackish - delay of 2000 milliseconds (2 seconds) before returning the values.
                    // This is to give the backend time to process the request and actually stores the data.
                    return of(null).pipe(
                        delay(2000),
                        switchMap(() => {
                            return [createLoadingEnded("sign-up-form"), signUpSuccess(email, password)]
                        })
                    )
                }),
                catchError(error => {
                    openNotification(NotificationType.Error, "Sign Up not successful.", `${error}`)
                    return [createLoadingEnded("sign-up-form"), signUpFailure(error)]
                })
            )
        })
    )

export const signUpSuccessEpics = (
    actions$: Observable<Action>,
    state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(SIGN_UP_SUCCESS),
        switchMap((action: Action) =>
            defer(() => deps.api.login({}, action.payload.email, action.payload.password)).pipe(
                switchMap((response: LoginResponsePayload) => [
                    signInSuccess(response.session, response.role, response.user_id, action.payload.email),
                ]),
                catchError(error =>
                    from([signInFailure(error, "The email and password you entered did not match our records.")])
                )
            )
        )
    )

export const signUpFailureEpics = (actions$: Observable<Action>) =>
    actions$.pipe(
        ofType(SIGN_UP_FAILURE),
        switchMap((action: Action) => {
            Router.push("/sign-up?error=" + action.payload.error)
            window.scrollTo(0, 0)
            return EMPTY
        })
    )
export default [signUpSubmitted, signUpRequested, signUpSuccessEpics, signUpFailureEpics]
